import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { IAgent } from 'src/app/models/IAgent.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { AgentsService } from 'src/app/services/agents.service';
import { Log } from 'src/app/services/shared/log';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * Modal state for the agent list page.
 *
 *   `null`    no modal, the table is the only thing visible
 *   `view`    read-only details panel + URL operations
 *   `edit`    reactive form with editable fields, save updates
 *   `delete`  destructive confirm (only if delete endpoint is wired)
 */
type AgentModalMode = 'view' | 'edit' | 'delete' | null;

/**
 * Accesso Funzionari — single-page agents list.
 *
 * Lists every agent on `/accesso-funzionari`. Click on a row opens the
 * *details* modal (info + the personal tracking URL + a button to send
 * the URL by email). A separate "Ordini" action on each row opens the
 * orders drawer (which mounts `<app-agente-list>` — that widget is
 * deliberately NOT rewritten in this restyle, its 1000-LOC refactor is
 * scheduled for the dedicated /agentsorders restyle later).
 *
 * Audience: Admin + SuperAdmin.
 *
 * Architectural notes / future work:
 *  - `OpenModalService.isDetAgent` / `isOrderOpen` are no longer used
 *    to coordinate modal visibility — this component owns its own
 *    modal state (`modalMode` + `showOrdersDrawer`). The service still
 *    exists for legacy callers but those are decreasing each release.
 *  - `Renderer2` + `ElementRef` trick from the legacy `copyToClipboard`
 *    is gone — we use `navigator.clipboard.writeText()` which is widely
 *    supported on every browser the warehouse still uses.
 *  - `markForCheck()` is applied after every async state mutation,
 *    consistent with the pattern used in messaggi + gestione-account
 *    (HTTP responses on this codebase don't reliably re-enter the
 *    Angular zone on this route family).
 *  - Agent CRUD: only `updateAgent` is exposed in this restyle. The
 *    backend supports it. Create (new agent) / Delete (remove agent)
 *    were not in the legacy flow and are not in scope here; if needed
 *    they can land as a small follow-up commit.
 */
@Component({
  selector: 'app-funzionari-list',
  templateUrl: './accesso-funzionari-list.component.html',
  styleUrls: ['./accesso-funzionari-list.component.scss'],
  standalone: false,
})
export class AccessoFunzionariListComponent implements OnInit {
  private readonly agentsService = inject(AgentsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';
  public filterTerm = '';
  public selectedAgentt: string | null = null;

  // ── Data ──────────────────────────────────────────────────────────
  agents: IAgent[] = [];

  // ── UI state ──────────────────────────────────────────────────────
  searchText = '';
  currentPage = 0;
  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';
  static readonly PAGE_SIZE = 20;

  // ── Modal / drawer state ─────────────────────────────────────────
  modalMode: AgentModalMode = null;
  selectedAgent: IAgent | null = null;
  agentForm!: FormGroup;
  /** Drawer with the embedded `<app-agente-list>` for the selected agent. */
  showOrdersDrawer = false;
  /** Token (URL-encoded) that the embedded `<app-agente-list>` consumes. */
  selectedAgentToken = '';

  // ── Async flags ───────────────────────────────────────────────────
  loading = false;
  saving = false;
  generatingUrl = false;
  deletingUrl = false;
  copying = false;

  private readonly clientToken = environment.CLIENT_TOKEN;

  /** Origin used to build tracking links (dev / staging / prod safe). */
  private readonly origin = window.location.origin;

  ngOnInit(): void {
    this.agentForm = this.buildForm();
    this.loadAgents();
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived state.
  // ──────────────────────────────────────────────────────────────────

  /** Agents after applying the searchText filter. */
  get filteredAgents(): IAgent[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.agents;
    return this.agents.filter((a) => this.haystack(a).includes(q));
  }

  /** Slice for the current page. */
  get pagedAgents(): IAgent[] {
    const start = this.currentPage * AccessoFunzionariListComponent.PAGE_SIZE;
    const sorted = this.sortField
      ? [...this.filteredAgents].sort((a, b) => {
          const rawA = (a as any)[this.sortField] ?? '';
          const rawB = (b as any)[this.sortField] ?? '';
          const va = rawA !== '' && !isNaN(Number(rawA)) ? Number(rawA) : rawA;
          const vb = rawB !== '' && !isNaN(Number(rawB)) ? Number(rawB) : rawB;
          const cmp = va < vb ? -1 : va > vb ? 1 : 0;
          return this.sortDir === 'asc' ? cmp : -cmp;
        })
      : this.filteredAgents;
    return sorted.slice(
      start,
      start + AccessoFunzionariListComponent.PAGE_SIZE,
    );
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(
        this.filteredAgents.length / AccessoFunzionariListComponent.PAGE_SIZE,
      ),
    );
  }

  get hasAgents(): boolean {
    return this.agents.length > 0;
  }

  /** True if the selected agent has a personal URL set on the backend. */
  get selectedHasUrl(): boolean {
    return !!this.selectedAgent?.ag_url;
  }

  /** Full personal tracking URL. Uses /clientorders for the client token, /agentsorders for all others. */
  get selectedFullUrl(): string {
    const token = this.selectedAgent?.ag_url;
    if (!token) return '';
    const route = token === this.clientToken ? 'clientorders' : 'agentsorders';
    return `${this.origin}/${route}?token=${encodeURIComponent(token)}`;
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  /** Two-character initials, used in the avatar. */
  initials(agent: IAgent): string {
    const first = (agent.ag_first_name || '').trim();
    const last = (agent.ag_last_name || '').trim();
    const a = first ? first[0] : '';
    const b = last ? last[0] : '';
    return (a + b).toUpperCase() || '?';
  }

  /**
   * Backend uses 'S' / 'N' for the active flag, but the field is
   * optional in IAgent and historically NOT populated for many
   * records — `ag_active === 'S'` would mark every legacy agent as
   * "Disattivato" purely because of missing data. The sane default
   * for the UI is "active unless explicitly marked 'N'": only an
   * intentional deactivation should be reflected in the pill.
   */
  isActive(agent: IAgent): boolean {
    return agent.ag_active !== 'N';
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions — table.
  // ──────────────────────────────────────────────────────────────────

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.currentPage = 0;
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  onSearchInput(): void {
    this.currentPage = 0;
  }

  clearSearch(): void {
    if (!this.searchText) return;
    this.searchText = '';
    this.currentPage = 0;
  }

  prevPage(): void {
    if (this.currentPage > 0) this.currentPage -= 1;
  }
  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) this.currentPage += 1;
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions — modal / drawer openers.
  // ──────────────────────────────────────────────────────────────────

  openView(agent: IAgent): void {
    this.selectedAgent = agent;
    this.modalMode = 'view';
  }

  openEdit(agent: IAgent): void {
    this.selectedAgent = agent;
    this.agentForm = this.buildForm(agent);
    this.modalMode = 'edit';
  }

  closeModal(): void {
    this.modalMode = null;
    this.selectedAgent = null;
  }

  /** Open the side drawer with `<app-agente-list>` for this agent. */
  openOrders(agent: IAgent): void {
    this.selectedAgent = agent;
    // Pass the RAW ag_url to AgenteList — Angular's HttpClient already
    // URL-encodes query params when serializing the backend request, so
    // encoding here too would double-encode (e.g. "%2B" → "%252B") and
    // the backend would not match the token. The legacy
    // `encodeURIComponent` was needed only when building a copy-paste
    // URL for the agent email (different code path).
    this.selectedAgentToken = agent.ag_url ?? '';
    this.showOrdersDrawer = true;
  }

  closeOrders(): void {
    this.showOrdersDrawer = false;
    this.selectedAgent = null;
    this.selectedAgentToken = '';
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions — persistence.
  // ──────────────────────────────────────────────────────────────────

  /** Save agent edits via `updateAgent`. */
  save(): void {
    if (this.saving || !this.selectedAgent) return;
    if (this.agentForm.invalid) {
      this.agentForm.markAllAsTouched();
      return;
    }
    // Form `ag_active` is a boolean (drives the checkbox in the
    // template), but the backend expects the string 'S' / 'N'. Spreading
    // the raw form value would send `true` / `false` and silently
    // mis-persist the flag. Convert explicitly here.
    const v = this.agentForm.value;
    const payload: IAgent = {
      ...this.selectedAgent,
      ag_first_name: v.ag_first_name?.trim() ?? '',
      ag_last_name: v.ag_last_name?.trim() ?? '',
      ag_email: v.ag_email?.trim() ?? '',
      ag_phone: v.ag_phone ?? '',
      ag_mobile: v.ag_mobile ?? '',
      ag_active: v.ag_active ? 'S' : 'N',
      ag_note: v.ag_note ?? '',
    };
    this.saving = true;
    this.agentsService
      .updateAgent(payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: IWsResult) => {
          if (!result?.success) {
            this.swalError(
              'Salvataggio fallito',
              'Il backend ha rifiutato la richiesta.',
            );
            return;
          }
          this.closeModal();
          // Patch the in-memory record instead of refetching. The backend's
          // getAgents endpoint filters out ag_active='N' by default, so a
          // freshly-disabled agent would disappear from the list and leave
          // the admin without a way to find / re-enable it. Same pattern
          // applied to /accounts (gestione-account-list).
          // TODO: when the backend exposes `?includeInactive=true`, add a
          // 'Mostra anche disattivati' toggle and restore loadAgents().
          this.applyAgentUpdate(payload);
        },
        error: (err) => {
          Log.error('agents: update failed', err);
          this.swalError('Errore di rete', 'Salvataggio non completato.');
        },
      });
  }

  /** Ask the backend to generate a personal tracking URL for the agent. */
  generateUrl(): void {
    if (this.generatingUrl || !this.selectedAgent) return;
    const id = String(this.selectedAgent.agent_id);
    this.generatingUrl = true;
    this.agentsService
      .generateURL(id)
      .pipe(
        finalize(() => {
          this.generatingUrl = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: IWsResult) => {
          if (!result?.success) {
            this.swalError(
              'Generazione fallita',
              'Il backend non ha generato il link.',
            );
            return;
          }
          // Refresh the list so `ag_url` updates everywhere, then re-open
          // the view on the same agent so the URL panel reflects the
          // new state without forcing the user to re-click.
          this.refreshAndReopen(id);
        },
        error: (err) => {
          Log.error('agents: generateURL failed', err);
          this.swalError('Errore di rete', 'Generazione non completata.');
        },
      });
  }

  /** Delete the agent's personal tracking URL. */
  deleteUrl(): void {
    if (this.deletingUrl || !this.selectedAgent) return;
    const id = String(this.selectedAgent.agent_id);

    Swal.fire({
      icon: 'warning',
      title: 'Eliminare il link?',
      html: `L'agente non potrà più accedere con il link corrente. Sicuro?`,
      showCancelButton: true,
      confirmButtonText: 'Sì, elimina',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#ef4444',
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.deletingUrl = true;
      this.agentsService
        .deleteURL(id)
        .pipe(
          finalize(() => {
            this.deletingUrl = false;
            this.cdr.markForCheck();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: (result: IWsResult) => {
            if (!result?.success) {
              this.swalError(
                'Eliminazione fallita',
                'Il backend ha rifiutato la richiesta.',
              );
              return;
            }
            this.refreshAndReopen(id);
          },
          error: (err) => {
            Log.error('agents: deleteURL failed', err);
            this.swalError('Errore di rete', 'Eliminazione non completata.');
          },
        });
    });
  }

  /**
   * Copy the agent's personal URL to the clipboard. Uses the modern
   * `navigator.clipboard.writeText()` API instead of the legacy
   * Renderer/textarea/execCommand trick that lived here before.
   */
  copyUrlToClipboard(): void {
    if (this.copying || !this.selectedAgent) return;
    const url = this.selectedFullUrl;
    if (!url) return;
    this.copying = true;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Link copiato',
          timer: 1200,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      })
      .catch((err) => {
        Log.error('agents: clipboard write failed', err);
        this.swalError(
          'Copia fallita',
          'Impossibile copiare il link negli appunti.',
        );
      })
      .finally(() => {
        this.copying = false;
        this.cdr.markForCheck();
      });
  }

  /** Open the user's default mail client with the URL pre-filled in the body. */
  sendUrlByEmail(): void {
    if (!this.selectedAgent) return;
    const url = this.selectedFullUrl;
    if (!url) return;
    const subject = encodeURIComponent('Link di accesso Diakonix');
    const body = encodeURIComponent(
      `Ciao ${this.selectedAgent.ag_first_name ?? ''},\n\n` +
        `Questo è il tuo link di accesso a Diakonix:\n${url}\n\n` +
        `A presto.`,
    );
    const mailto = `mailto:${this.selectedAgent.ag_email}?subject=${subject}&body=${body}`;
    window.open(mailto, '_self');
  }

  // ──────────────────────────────────────────────────────────────────
  // Private helpers.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Replace an in-memory agent record by `agent_id`. Used after a
   * successful `updateAgent` so the row stays visible (and reflects
   * the just-saved values) even if the backend would have hidden it
   * on the next list refresh (e.g. when ag_active was flipped to 'N').
   */
  private applyAgentUpdate(updated: IAgent): void {
    const idx = this.agents.findIndex(
      (a) => String(a.agent_id) === String(updated.agent_id),
    );
    if (idx < 0) return;
    this.agents = [
      ...this.agents.slice(0, idx),
      { ...this.agents[idx], ...updated },
      ...this.agents.slice(idx + 1),
    ];
    this.cdr.markForCheck();
  }

  private loadAgents(): void {
    this.loading = true;
    this.agentsService
      .getAgents()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: IWsResult) => {
          // Backend uses `msg_code === 8` to signal "no rows".
          this.agents =
            result?.error_message?.msg_code === 8 ? [] : (result?.data ?? []);
          if (this.currentPage >= this.totalPages) {
            this.currentPage = this.totalPages - 1;
          }
          this.cdr.markForCheck();
        },
        error: (err) => Log.error('agents: load failed', err),
      });
  }

  /**
   * Reload the agent list and re-select the same agent in the view modal
   * (used after `generateURL` / `deleteURL` so the URL panel reflects
   * the new state without forcing the admin to re-click on the row).
   */
  private refreshAndReopen(agentId: string): void {
    this.agentsService
      .getAgents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          this.agents =
            result?.error_message?.msg_code === 8 ? [] : (result?.data ?? []);
          // Re-bind selectedAgent to the fresh record so the template
          // shows the new ag_url immediately.
          const fresh = this.agents.find((a) => String(a.agent_id) === agentId);
          if (fresh) this.selectedAgent = fresh;
          this.cdr.markForCheck();
        },
        error: (err) => Log.error('agents: refresh-and-reopen failed', err),
      });
  }

  /** Reactive form for the edit modal (no create flow yet). */
  private buildForm(agent?: IAgent): FormGroup {
    return this.fb.group({
      ag_first_name: [agent?.ag_first_name ?? '', [Validators.required]],
      ag_last_name: [agent?.ag_last_name ?? '', [Validators.required]],
      ag_email: [
        agent?.ag_email ?? '',
        [Validators.required, Validators.email],
      ],
      ag_phone: [agent?.ag_phone ?? ''],
      ag_mobile: [agent?.ag_mobile ?? ''],
      ag_active: [(agent?.ag_active ?? 'S') === 'S'],
      ag_note: [agent?.ag_note ?? ''],
    });
  }

  /** Lower-cased haystack for client-side search across an agent's fields. */
  private haystack(agent: IAgent): string {
    return [
      agent.ag_first_name,
      agent.ag_last_name,
      agent.ag_email,
      agent.ag_phone,
      agent.ag_mobile,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  /** Centralised error toast — keeps the call sites short. */
  private swalError(title: string, text: string): void {
    Swal.fire({
      icon: 'error',
      title,
      text,
      timer: 2200,
      timerProgressBar: true,
    });
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }

  get sortedAgents(): IAgent[] {
    if (!this.sortColumn) return this.filteredAgents;

    return sortItems(this.filteredAgents, this.sortColumn, this.sortDirection);
  }
}
